'use client'

import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { Fragment } from 'react'

// Centered dialog. Headless UI's Dialog supplies the parts a modal must
// have and is easy to get wrong by hand: focus trap, ESC and click-outside
// to close, aria-modal + labelling, background scroll lock, focus returned
// to the trigger on close.
//
// Pass `title` for content that needs a heading and can run long (a list):
// the panel becomes a fixed header over a scrolling body, capped at 85vh so
// the dialog never runs off the viewport. Without it the modal keeps its
// original padded-box shape.
export function BasicModal({
  isOpen,
  onClose,
  children,
  className = '',
  title = null,
}) {
  const baseClasses = title
    ? 'bg-background border-2 border-foreground w-full rounded-sm shadow-2xl flex flex-col max-h-[85vh]'
    : 'bg-background border-2 border-foreground w-full p-6 rounded-sm shadow-2xl'

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm" />
        </TransitionChild>

        {/* Modal Container */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className={clsx(baseClasses, className)}>
              {title ? (
                <>
                  <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-border flex-shrink-0">
                    <DialogTitle className="font-serif text-lg font-medium leading-snug">
                      {title}
                    </DialogTitle>
                    <button
                      type="button"
                      onClick={onClose}
                      className="-mt-1 -mr-1 rounded-sm p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
                      aria-label="Close modal"
                    >
                      <XMarkIcon aria-hidden="true" className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="overflow-y-auto">{children}</div>
                </>
              ) : (
                <>
                  <div className="flex justify-end -mt-2 -mr-2 mb-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-sm p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      aria-label="Close modal"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                    </button>
                  </div>
                  {children}
                </>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
